import React, { useEffect, useState } from "react";
import {
  AccountCard,
  Backdrop,
  CopyToClipboard,
  CustomSearchNew,
  LogoLoader,
  showToast,
} from "../../../../components";
import { dateFormator, mapTableRows } from "../../../../utils";
import { useDispatch, useSelector } from "react-redux";
import {
  getRaSettingsStart,
  addJobStart,
} from "../../../../redux/slices/leads/leadsSlice";
import { debounce } from "lodash";

import {
  addCompanyApi,
  fetchCompanies,
  fetchCompanyByUiid,
  fetchJobByFilters,
  fetchProspects,
} from "../../../../redux/slices/leads/leadsApi";
import common from "../../../../utils/common";

const BdmLeadProfile = () => {
  const { classifyInput } = common;
  const [searchValue, setSearchValue] = useState("");
  const [filteredCount, setFilteredCount] = useState(0);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [defaultWebsite, setDefaultWebsite] = useState("");
  const [defaultJobLocation, setDefaultJobLocation] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [searchType, setSearchType] = useState("");
  const [selectLocation, setSelectLocation] = useState(null);
  const [isEmptyGoogleResponse, setIsEmptyGoogleResponse] = useState(false);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state?.auth);
  const raSettings = useSelector((state) => state?.leads?.raSettings?.data);
  const [searchedEmail, setSearchedEmail] = useState("");

  // selected prospects
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [selectedProspectIds, setSelectedProspectIds] = useState([])

  const currentCompanyState = useSelector(
    (state) => state?.leads?.company?.current
  );
  // Debounced search function
  const debouncedSearch = React.useMemo(
    () =>
      debounce(async (value) => {
        if (value.trim() !== "" && value.length >= 2) {
          setIsSearching(true);
          const name = value.trim().toLowerCase();
          const type = classifyInput(name);
          setSearchType(type);
          let results = [];

          try {
            switch (type) {
              case "Email": {
                const response = await fetchProspects({
                  // filters: { "email.primary": { type: "SEARCH", value: name } },
                  filters: { email: { type: "SEARCH", value: name } },
                  limit: 10,
                  page: 1,
                });

                results = (response.data?.data || []).map((data) => ({
                  ...data,
                  from: "internal",
                }));
                break;
              }
              case "Domain": {
                const response = await fetchCompanies({
                  filters: { domain: { type: "SEARCH", value: name } },
                  limit: 10,
                  page: 1,
                  ret: "uiid name domain industry status addedBy addedOn",
                });

                results = (response.data?.data || []).map((company) => ({
                  ...company,
                  from: "internal",
                }));
                break;
              }
              case "Company":
              default: {
                const response = await fetchCompanies({
                  // filters: { name: { type: "SEARCH", value: name } },
                  filters: {
                    name: {
                      type: "SEARCH",
                      value: name,
                      addedBy: user?.data?.uiid,
                    },
                  },
                  limit: 10,
                  page: 1,
                  // postActions: ["GoogleSearch"],
                  ret: "uiid name domain industry status addedBy addedOn",
                });

                if (response?.data?.data?.length > 0) {
                  results = response?.data?.data.map((company) => ({
                    ...company,
                    from: "internal",
                  }));
                }
                // else {
                //   const googleResults = response.data?.field?.find(
                //     (f) => f.action === "GoogleSearch"
                //   )?.result?.response;

                //   results = Array.isArray(googleResults)
                //     ? googleResults.map((companyName) => ({
                //         name: companyName,
                //         from: "linkedin",
                //         uiid: null, // No uiid for external results
                //       }))
                //     : [];
                // }
                else {
                  // const googleField = response.data?.field?.find(
                  //   (f) => f.action === "GoogleSearch"
                  // );
                  const field = response?.data?.field;
                  const googleField = Array.isArray(field)
                    ? field.find((f) => f.action === "GoogleSearch")
                    : null;
                  const googleResult = googleField?.result;
                  const googleResponse = googleResult?.response;

                  if (
                    googleResponse &&
                    typeof googleResponse === "object" &&
                    !Array.isArray(googleResponse) &&
                    googleResponse.message
                  ) {
                    showToast(googleResponse?.message, "info");
                    results = [];
                  } else if (Array.isArray(googleResponse)) {
                    if (googleResponse.length === 0) {
                      setIsEmptyGoogleResponse(true);
                      showToast("No companies found in Google Search.", "info");
                      results = [];
                    } else {
                      setIsEmptyGoogleResponse(false);
                      results = googleResponse.map((companyName) => ({
                        name: companyName,
                        from: "linkedin",
                        uiid: null,
                      }));
                    }
                  }
                }
                break;
              }
            }
            if (results.length === 0) {
              setSelectedValue(value);
              setCurrentCompany({ uiid: null });
            }
            setSearchResults(results);
          } catch (err) {
            console.error("Search error:", err);
            setSearchResults([]);
          } finally {
            setIsSearching(false);
          }
        } else {
          setSearchResults([]);
          setIsSearching(false);
        }
      }, 300),
    [classifyInput]
  );

  const toggleAddAccountModal = async () => {
    if (!searchValue.trim()) {
      return;
    }
    setIsLoadingCompany(true);
    try {
      const payload = {
        coll: "Company",
        payload: {
          name: searchValue.trim().toLowerCase(), // Using .toLowerCase() correctly
          addedBy: user?.data?.uiid, // Assuming user.data.uiid is valid
        },
      };
      const response = await addCompanyApi({ payload });
      if (response.success) {
        showToast("Company added successfully!", "success", 2000);
        setShowAddAccountModal(true);
        const data = response.data.data || {}; // Defaulting to an empty object if no data
        setCurrentCompany((prevState) => ({ ...prevState, ...data }));
        setIsLoadingCompany(false);
      } else {
        setIsLoadingCompany(false);
      }
    } catch (error) {
      showToast("Failed to add company. Please try again.", "error", 2000);
      setIsLoadingCompany(false);
    }
  };

  const closeAccountModal = (runApi = false) => {
    const payload = {
      coll: "Job",
      postActions: ["AddToProspect"], // no Prospects
      payload: {
        addedBy: user?.data?.uiid,
        companyId: currentCompany?.uiid,
      },
    };

    !runApi &&
      dispatch(
        addJobStart({
          payload,
          onSuccess: () => { },
        })
      );

    setSearchValue("");
    setCurrentCompany(null);
    setSelectedValue("");
    setShowAddAccountModal(false);
    setSearchResults([]);
  };

  // Fixed handleSelectSuggestion function
  const handleSelectSuggestion = async (value) => {
    setSelectedValue(searchType == "Email" ? value?.email?.email : value?.name);
    setSearchValue(searchType == "Email" ? value?.email?.email : value?.name);
    setSearchResults([]); // Clear search results
    // Case 1: Item has uiid - fetch company data from DB
    if (value.uiid) {
      setIsLoadingCompany(true);
      const selectCompanyId =
        searchType == "Email" ? value.companyId : value.uiid;
      try {
        const filters = { uiid: { type: "NONE", value: selectCompanyId } };
        const response = await fetchCompanyByUiid({
          filters,
          ref: "JobsWithProspect",
          extret: "uiid firstName",
        });

        if (response?.success && response?.data?.data) {
          // Company found with data
          setCurrentCompany(response?.data?.data);
        } else {
          // Company exists but no job/prospect data
          setCurrentCompany(null);
        }
      } catch (err) {
        console.error("Error fetching company:", err);
        setCurrentCompany(null);
      } finally {
        setIsLoadingCompany(false);
      }
    } else {
      // Case 2: Item has no uiid - external result (LinkedIn, etc.)
      setCurrentCompany({ ...value });
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);

    // Clear current company and selection if value changes
    if (value !== selectedValue) {
      setCurrentCompany(null);
      setSelectedValue("");
    }

    if (!value.trim()) {
      setSearchResults([]);
      setCurrentCompany(null);
      setSelectedValue("");
    }
  };

  // const handleAddJobClick = ({ show, website, location }) => {
  //   setShowAddAccountModal(show);
  //   setDefaultWebsite(website);
  //   setDefaultJobLocation(location);
  // };
  const handleAddJobClick = ({ show, website, location }) => {
    setShowAddAccountModal(show);
    setDefaultWebsite(website);
    setDefaultJobLocation(location);

    // remove the Add Job button flag
    setCurrentCompany((prev) => ({
      ...prev,
      noJobsInLocation: false,
    }));
  };

  const colRADetails = [
    {
      key: "name",
      label: "RA Name",
    },
    {
      key: "addedOn",
      label: "Added On",
      render: (row) => {
        const { addedOn } = row;
        return addedOn ? dateFormator(addedOn) : "";
      },
    },
    {
      key: "jobTitle",
      label: "Job Title",
    },
    {
      key: "jobLocation",
      label: "Job Location",
    },
    {
      key: "jobDescription",
      label: "Job Description",
      render: (row) => {
        const { jobDescription } = row;
        return jobDescription ? (
          <a
            href={jobDescription}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline cursor-pointer"
          >
            Link
          </a>
        ) : (
          ""
        );
      },
    },
  ];

  const colContactDetails = [
    {
      key: "name",
      label: "Full Name",
      render: (row) => {
        const { name } = row;
        if (typeof name === "object" && name !== null) {
          return `${name?.firstName || ""} ${name?.middleName || ""}`.trim();
        }
        return name || "";
      },
    },
    {
      key: "designation",
      label: "Designation",
    },
    {
      key: "location",
      label: "Location",
    },
    {
      key: "email",
      label: "Email",
      render: (row) => {
        const { email, status } = row;
        const emailStatus = {
          Verified: "success",
          Positive: "success-300",
          DND: "warning",
          "No Follow-up": "dark",
          Blocked: "danger",
          "Not Verified": "primary",
        };
        return email ? (
          <div
            className={`flex items-center gap-0.5 text-${emailStatus[status]}`}
          >
            <CopyToClipboard
              content={email}
              title={email}
              color={emailStatus[status]}
            />
            {email}
            {status === "Verified" && (
              <div className="ml-2 w-3 h-3 rounded-full bg-success  flex items-center justify-center">
                <span className="material-symbols-outlined text-white !text-[10px]">
                  check
                </span>
              </div>
            )}
          </div>
        ) : (
          ""
        );
      },
    },
    {
      key: "phone",
      label: "Phone",
      render: (row) => {
        const { phone } = row;
        return phone ? (
          <div className="flex items-center gap-0.5">
            <CopyToClipboard content={phone} title={phone} />
            {phone}
          </div>
        ) : (
          ""
        );
      },
    },
  ];

  const statusList = ["Positive", "Verified", "DND", "Not Verified"];
  const companyStatus = {
    1: "Active",
    2: "Blocked",
  };
  const getLockInPeriod = ({ addedOn, lockDays, text = false }) => {
    if (!addedOn) return true;
    const addedDate = new Date(addedOn);
    const today = new Date();
    const diffTime = today - addedDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= lockDays
      ? text
        ? "Company Unlocked"
        : true
      : text
        ? `${lockDays - diffDays} days left to unlock`
        : false;
  };

  // Updated logic for showing company details and add button
  const showCompanyDetails =
    currentCompany && currentCompany?.uiid && selectedValue === searchValue;

  const showAddAccount =
    !isSearching && // not currently searching
    !isLoadingCompany && // not loading company data
    currentCompany && // company is selected
    !currentCompany?.uiid && // but has no job/prospect data
    selectedValue === searchValue && // and matches current selection
    searchResults.length == 0; // and no search results

  if (showAddAccount) {
    // console.log("Condition for showAddAccount is true");
  }

  // const handleLocationChange = async (locationValue) => {
  //   if (!currentCompany) return;
  //   setSelectLocation(locationValue);
  //   const [cityRaw, stateRaw] = (locationValue || "").split("-");
  //   const city = cityRaw?.trim();
  //   const state = stateRaw?.trim();
  //   try {
  //     setIsLoadingCompany(true);
  //     const response = await fetchJobByFilters({
  //       companyId: currentCompany?.uiid,
  //       state,
  //       city,
  //       ref: "Propects",
  //     });
  //     const jobs = response?.data || [];
  //     if (jobs.length > 0) {
  //       setCurrentCompany({
  //         ...currentCompany,
  //         jobIds: jobs,
  //       });
  //     } else {
  //       setCurrentCompany({
  //         ...currentCompany,
  //         jobIds: [],
  //       });
  //     }
  //     setIsLoadingCompany(false);
  //   } catch (err) {
  //     setIsLoadingCompany(false);
  //   }
  // };

  // const handleLocationChange = async (locationValue) => {
  //   if (!currentCompany) return;

  //   setSelectLocation(locationValue);
  //   const [cityRaw, stateRaw] = (locationValue || "").split("-");
  //   const city = cityRaw?.trim();
  //   const state = stateRaw?.trim();

  //   try {
  //     setIsLoadingCompany(true);
  //     const response = await fetchJobByFilters({
  //       companyId: currentCompany?.uiid,
  //       state,
  //       city,
  //       ref: "Propects",
  //     });

  //     const jobs = response?.data || [];

  //     if (jobs.length > 0) {
  //       // jobs exist → update company with jobs
  //       setCurrentCompany({
  //         ...currentCompany,
  //         jobIds: jobs,
  //       });
  //     } else {
  //       // no jobs exist → open Add Job modal
  //       setCurrentCompany({
  //         ...currentCompany,
  //         jobIds: [],
  //       });

  //       handleAddJobClick({
  //         show: true,
  //         website: currentCompany?.domain,
  //         location: locationValue, // pass location to modal
  //       });
  //     }
  //   } catch (err) {
  //     console.error("Error fetching jobs by location:", err);
  //   } finally {
  //     setIsLoadingCompany(false);
  //   }
  // };

  const handleLocationChange = async (locationValue) => {
    if (!currentCompany) return;

    setSelectLocation(locationValue);
    const [cityRaw, stateRaw] = (locationValue || "").split("-");
    const city = cityRaw?.trim();
    const state = stateRaw?.trim();

    try {
      setIsLoadingCompany(true);
      const response = await fetchJobByFilters({
        companyId: currentCompany?.uiid,
        state,
        city,
        ref: "Propects",
      });

      const jobs = response?.data || [];

      if (jobs.length > 0) {
        setCurrentCompany({
          ...currentCompany,
          jobIds: jobs,
          noJobsInLocation: false,
          selectedLocation: locationValue, // no button needed
        });
      } else {
        // no jobs exist → show Add Job button instead of opening modal
        setCurrentCompany({
          ...currentCompany,
          jobIds: [],
          noJobsInLocation: true, // flag to show button
          selectedLocation: locationValue, // store selected location
        });
      }
    } catch (err) {
      console.error("Error fetching jobs by location:", err);
    } finally {
      setIsLoadingCompany(false);
    }
  };
  const handleManualSearch = async () => {
    if (searchValue.trim() === "") return;
    // manually trigger the same API logic as typing
    debouncedSearch.cancel(); // stop any pending debounce
    await debouncedSearch.flush?.(); // optional, executes any pending one
    debouncedSearch(searchValue); // trigger search
  };


  function extractJobAndProspects(doc) {
    const jobIds = [];
    const prospectIds = [];
    if (!doc) return { jobIds, prospectIds };
    if (Array.isArray(doc.jobIds)) {
      for (const job of doc.jobIds) {
        jobIds.push(job);
        if (Array.isArray(job.prospectIds)) {
          prospectIds.push(...job.prospectIds);
        }
      }
    }
    return { jobIds, prospectIds };
  }
  const EMAIL_STATUS_MAP = {
    1: "Not Verified",
    2: "Verified",
    3: "Invalid",
  };

  const INPERSON_STATUS_MAP = {
    1: "Not Responded",
    2: "Positive",
    3: "DND",
  };

  const getProspectStatus = (prospect) => {
    if (prospect?.inpersonResponseStatus !== 1) {
      return INPERSON_STATUS_MAP[prospect?.inpersonResponseStatus] || "Unknown";
    }
    // return EMAIL_STATUS_MAP[prospect.emailVerificationStatus] || "Unknown";
    return EMAIL_STATUS_MAP[prospect?.email?.status] || "Unknown";
  };
  const { jobIds, prospectIds } = extractJobAndProspects(currentCompany);
  const rowsContactDetails =
    prospectIds.map((prospect) => ({
      id: prospect?._id,
      name: {
        firstName: prospect?.firstName || "",
        middleName: prospect?.lastName || "",
      },
      designation: prospect?.designation || "N/A",
      // location: prospect.location
      //   ? `${prospect.location.city || ""}, ${prospect.location.state || ""}, ${prospect.location.country || ""
      //     }`.trim()
      //   : "N/A",
      location: prospect?.location
        ? [
          prospect?.location?.city,
          prospect?.location?.state,
          prospect?.location?.country,
        ]
          .filter(Boolean)
          .join(", ") || "N/A"
        : "N/A",
      email: prospect?.email?.email || "",
      // phone: prospect.mobile?.primary
      //   ? `${prospect.mobile.primary.code} ${prospect.mobile.primary.number}`
      //   : "",
      phone: prospect?.mobile
        ? `${prospect?.mobile?.code || ""} ${prospect?.mobile?.number || ""}`.trim()
        : "",

      // status:
      //   prospect.status === 1
      //     ? "Verified"
      //     : prospect.status === 3
      //       ? "Not Verified"
      //       : "Unknown",
      status: getProspectStatus(prospect),
    })) || [];
  const rowRADetails = jobIds.map((data) => {
    return {
      id: data._id,
      name: data?.addedBy?.firstName || "",
      uiid: data?.uiid,
      addedOn: data?.addedOn || "",
      jobTitle: data?.title || "",
      // jobLocation: data.location
      //   ? `${data.location.city || ""}, ${data.location.state || ""}, ${data.location.country || ""
      //     }`.trim()
      //   : "N/A",
      jobLocation: data?.location
        ? [data?.location?.city, data?.location?.state, data?.location?.country]
          .filter(Boolean) // keeps only truthy values
          .join(", ") || "N/A" // fallback if all are falsy
        : "N/A",
      jobDescription: data?.description || "",
    };
  });
  const mappedRowsRADetails = mapTableRows(rowRADetails, colRADetails);
  const mappedRowsContactDetails = mapTableRows(
    rowsContactDetails,
    colContactDetails
  );
  const lockInPeriodTime = getLockInPeriod({
    addedOn: jobIds[jobIds.length - 1]?.addedOn,
    lockDays: raSettings?.[0]?.raSetting?.waitingPeriod,
    text: true,
  });

  useEffect(() => {
    dispatch(
      getRaSettingsStart({
        coll: "Setting",
        lim: 10,
        pg: 1,
        ret: "raSetting uiid",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    setSelectedProspectIds([])
  }, [])

  // log every time data changes
  // useEffect(() => {
  //   console.log("Fetched RA Settings from Redux:", raSettings);
  // }, [raSettings]);

  const onSelectionChange = (e) => {
    const selectedOnes = rowsContactDetails
      .slice(0, 5)
      .filter(item => e.includes(item.id) && item.status !== "DND")
      .map(item => ({
        firstName: item?.name?.firstName,
        lastName: item?.name?.middleName,
        email: item?.email,
        location: item?.location,
        designation: item?.designation,
        mobile: { code: item?.phone?.code, number: item?.phone?.number }
      }));
    const selectedIds = rowsContactDetails
      .filter(item => e.includes(item.id) && item.status !== "DND")
      .map(item => item.id);

    setSelectedProspects(selectedOnes);
    setSelectedProspectIds(selectedIds)
  }

  return (
    <div className="flex-1">
      <CustomSearchNew
        value={searchValue}
        onChange={handleInputChange}
        onFilteredCountChange={(count) => setFilteredCount(count)}
        names={searchResults}
        onSelect={handleSelectSuggestion}
        searchType={searchType}
        onSearch={() => handleManualSearch()}
      />
      <>
        {showCompanyDetails ? (
          <>
            <div className="w-full flex justify-center">
              <div className="w-full max-w-7xl">
                <AccountCard
                  data={{
                    accountName: currentCompany?.name,
                    accountStatus:
                      companyStatus[currentCompany?.status] || "Unknown",
                    industry: currentCompany?.industry || "N/A",
                    website: currentCompany?.domain,
                    location: jobIds[jobIds.length - 1]?.location
                      ? `${jobIds[jobIds.length - 1]?.location?.city || ""}-${jobIds[jobIds.length - 1].location?.state || ""
                      }`
                      : selectLocation,
                    lastJobStatus: jobIds[jobIds.length - 1]?.status || 1,
                  }}
                  currentCompany={currentCompany}
                  handleAddNewLoc={handleAddJobClick}
                  lockInPeriodTime={getLockInPeriod({
                    addedOn: jobIds[jobIds.length - 1]?.addedOn || currentCompany?.addedOn,
                    lockDays: raSettings?.[0]?.raSetting?.waitingPeriod || 18,
                    text: false,
                  })}
                  //chnage3
                  onLocationChange={handleLocationChange}
                  raSetting={raSettings?.[0]?.raSetting}
                  setCurrentCompany={setCurrentCompany}
                  isBdm={true}
                  searchType={searchType}
                  searchValue={searchValue}
                />
              </div>
            </div>
            {/* <div className="w-full overflow-hidden flex gap-10 mt-7 flex-1">
              {jobIds.length > 0 && (
                <div className="flex flex-1 flex-col overflow-auto">
                  <div>
                    <h6 className="header">RA Details</h6>
                  </div>
                  <NewTableAdv
                    columns={colRADetails}
                    rows={mappedRowsRADetails}
                    info={"info"}
                    tdClassName="!py-3"
                    toolbarRight={{
                      search: false,
                      filter: false,
                      download: false,
                    }}
                  />
                </div>
              )}
              {prospectIds?.length > 0 && (
                <div className="flex flex-1 flex-col overflow-auto">
                  <div>
                    <h6 className="header">Contact Details</h6>
                  </div>
                  <NewTableAdv
                    columns={colContactDetails}
                    rows={mappedRowsContactDetails}
                    onSelectionChange={onSelectionChange}
                    defaultSelectedRows={selectedProspectIds}
                    showCheckbox
                    info={"info"}
                    tdClassName="!py-3"
                    toolbarRight={{
                      search: false,
                      filter: false,
                      download: false,
                      add: [
                        {
                          component: (
                            <div className="flex gap-5">
                              {statusList.map((item) => {
                                const status = {
                                  Positive: "success-300",
                                  Verified: "success",
                                  DND: "warning",
                                  "No Follow-up": "dark",
                                  Blocked: "danger",
                                  "Not Verified": "primary",
                                };
                                return (
                                  <div
                                    key={item}
                                    className="flex items-center gap-1"
                                  >
                                    <div
                                      className={`w-2 h-2 rounded-full bg-${status[item]}`}
                                    ></div>
                                    <div>{item}</div>
                                  </div>
                                );
                              })}
                            </div>
                          ),
                        },
                      ],
                    }}
                  />
                </div>
              )}
            </div> */}
          </>
        ) : (
          showAddAccount &&
          !isEmptyGoogleResponse && (
            <div className="flex justify-center items-center flex-col gap-2 mb-10">
              <div>No Details Found under this Account.</div>
              {/* <div>
                <Button
                  title={"Add Account"}
                  icon={"add"}
                  onClick={toggleAddAccountModal}
                />
              </div> */}
            </div>
          )
        )}
        {/* <Modal
          isOpen={showAddAccountModal}
          onClose={closeAccountModal}
          width="w-full"
          title={`${formatWords2(currentCompany?.name)}`}
        >
          <AddAccount
            onClose={() => {
              closeAccountModal(true)
              setSelectedProspectIds([])
            }}
            defaultWebsite={defaultWebsite}
            defaultJobLocation={defaultJobLocation}
            currentCompany={currentCompany}
            //change1
            raSetting={raSettings?.[0]?.raSetting}
            selectedProspects={selectedProspects}
            enableLocationRestrictionCheck={true}
          />
        </Modal> */}
        <Backdrop isOpen={isLoadingCompany}>
          <LogoLoader />
        </Backdrop>
      </>
    </div>
  );
};

export default BdmLeadProfile;
